import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplaceEx, canActivateBidder } from "@front/m19-models";
import {
  AccountMarketplaceService,
  isReadOnlyAccess,
  OrganizationService,
  OrganizationUsersService,
} from "@front/m19-services";
import { IButtonDirective, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { finalize } from "rxjs";

@Component({
  selector: "app-activate-ads-automation-cta",
  imports: [TranslocoDirective, IButtonDirective],
  template: `
    @if (canActivateBidder() && isAdminOrOwner() && !isReadOnly()) {
      <button *transloco="let t" iButton size="sm" [loading]="loading()" (click)="activateBidder()">
        {{ t("account-setting.activate_full_automation") }}
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivateAdsAutomationCtaComponent {
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly organizationService = inject(OrganizationService);
  private readonly organizationUsersService = inject(OrganizationUsersService);

  readonly accountMarketplace = input.required<AccountMarketplaceEx>();

  private readonly organizations = toSignal(this.organizationService.organizations$);
  private readonly adminOrganizationIds = toSignal(this.organizationUsersService.adminOrganizationIds$);

  private readonly organization = computed(() =>
    this.organizations()?.find((o) => o.organizationId === this.accountMarketplace().resourceOrganizationId),
  );

  protected readonly isReadOnly = computed(() => isReadOnlyAccess(this.accountMarketplace().accessLevel));

  protected readonly isAdminOrOwner = computed(
    () => this.adminOrganizationIds()?.has(this.accountMarketplace().resourceOrganizationId!) ?? false,
  );

  protected readonly canActivateBidder = computed(() => {
    const organization = this.organization();
    return !!organization && canActivateBidder(organization, this.accountMarketplace());
  });

  protected readonly loading = signal(false);

  protected activateBidder(): void {
    this.loading.set(true);
    this.accountMarketplaceService
      .activateBidder([this.accountMarketplace()])
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate("activate-automation-cta.activate_ads_automation_success"),
          );
        },
        error: () => {
          this.toasterService.error(
            this.translocoService.translate("activate-automation-cta.activate_ads_automation_error"),
          );
        },
      });
  }
}
